import { throwIfAborted, type FrameRenderer, type FrameSession } from './types';

/** A mounted preview that can be held at a playhead. */
export type PreviewSeekHost = {
  /** Paint at `timeSeconds` (null restores the static design) and return the frame element. */
  seek: (timeSeconds: number | null, signal?: AbortSignal) => Promise<HTMLElement>;
};

export type DomRasterSession = {
  width: number;
  height: number;
  warnings: string[];
  rasterizeToCanvas: () => Promise<HTMLCanvasElement>;
  close: () => void;
};

export type OpenDomRasterSession = (
  element: HTMLElement,
  signal?: AbortSignal,
) => Promise<DomRasterSession>;

function waitUnlessAborted(ready: Promise<void>, signal?: AbortSignal): Promise<void> {
  if (!signal) {
    return ready;
  }
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      try {
        throwIfAborted(signal);
      } catch (error) {
        reject(error);
      }
    };
    if (signal.aborted) {
      onAbort();
      return;
    }
    signal.addEventListener('abort', onAbort, { once: true });
    ready.then(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    });
  });
}

/**
 * Sessions share one playhead, so each `open` waits until the previous session has
 * closed. A cancelled render can still be finishing its last frame when the next starts.
 */
export function createDomFrameRenderer(
  host: PreviewSeekHost,
  openRasterSession: OpenDomRasterSession,
): FrameRenderer {
  let tail: Promise<void> = Promise.resolve();

  return {
    async open(_scene, signal): Promise<FrameSession> {
      let release!: () => void;
      const released = new Promise<void>((resolve) => {
        release = resolve;
      });
      const ready = tail;
      tail = ready.then(() => released);

      let raster: DomRasterSession;
      try {
        await waitUnlessAborted(ready, signal);
        const element = await host.seek(0, signal);
        try {
          raster = await openRasterSession(element, signal);
        } catch (error) {
          await host.seek(null).catch(() => undefined);
          throw error;
        }
      } catch (error) {
        release();
        throw error;
      }
      return {
        size: { width: raster.width, height: raster.height },
        warnings: raster.warnings,
        async renderFrame(timeSeconds, frameSignal) {
          throwIfAborted(frameSignal ?? signal);
          await host.seek(timeSeconds, frameSignal ?? signal);
          throwIfAborted(frameSignal ?? signal);
          return raster.rasterizeToCanvas();
        },
        async close() {
          try {
            raster.close();
            await host.seek(null).catch(() => undefined);
          } finally {
            release();
          }
        },
      };
    },
  };
}
